import { Delete } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    AppRoleAssignment,
    AzureUsersModel,
    ModifyUsersInGroupsModel,
} from "src/App/api/APIClient";
import { userAzureService } from "src/App/api/Services";
import { usePanelProjectContext } from "src/App/BIMDigitalTwin/PanelProjectProvider";

type Row = {
    id: string;
    mail: string;
    name?: string;
    originalGroupId: string;
    selectedGroupId: string;
};

export default function AssociateUsersRoles({
    roles,
    associateRolesUsers,
    setAssociateRolesUsers,
}: {
    roles: AppRoleAssignment[];
    associateRolesUsers: ModifyUsersInGroupsModel[];
    setAssociateRolesUsers: React.Dispatch<React.SetStateAction<ModifyUsersInGroupsModel[]>>;
}) {
    const { t } = useTranslation("project");
    const { project } = usePanelProjectContext();
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);

    async function fetchUsersWithRoles() {
        try {
            const res = await userAzureService.getUsersFromAzureTenant({ projectId: project.id });
            const data: AzureUsersModel[] = res.data ?? [];
            setRows(
                data.map((u) => ({
                    id: String(u.id),
                    mail: u.mail ?? "",
                    name: u.name ?? "",
                    originalGroupId: u.groupId ?? "",
                    selectedGroupId: u.groupId ?? "",
                }))
            );
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchUsersWithRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // helpers para mantener associateRolesUsers
    const removeUserFromAllBuckets = (prev: ModifyUsersInGroupsModel[], userId: string) => {
        const next = prev
            .map((g) => ({ ...g, userIds: (g.userIds ?? []).filter((id) => id !== userId) }))
            .filter((g) => (g.userIds?.length ?? 0) > 0);
        return next;
    };

    const addUserToBucket = (
        prev: ModifyUsersInGroupsModel[],
        groupId: string,
        addUsers: boolean,
        userId: string
    ) => {
        const idx = prev.findIndex((g) => g.groupId === groupId && g.addUsers === addUsers);
        if (idx >= 0) {
            const exists = (prev[idx].userIds ?? []).includes(userId);
            if (exists) return prev;
            const clone = [...prev];
            clone[idx] = { ...clone[idx], userIds: [...(clone[idx].userIds ?? []), userId] };
            return clone;
        }
        return [...prev, { groupId, addUsers, userIds: [userId] }];
    };

    const recomputeUserContribution = (
        userId: string,
        originalGroupId: string,
        selectedGroupId: string
    ) => {
        setAssociateRolesUsers((prev) => {
            let next: ModifyUsersInGroupsModel[] = removeUserFromAllBuckets(prev, userId);
            const from = originalGroupId || "";
            const to = selectedGroupId || "";
            if (from === to) return next;
            if (from) next = addUserToBucket(next, from, false, userId);
            if (to) next = addUserToBucket(next, to, true, userId);
            return next;
        });
    };

    const updateRowRole = (userId: string, groupId: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === userId ? { ...r, selectedGroupId: groupId } : r))
        );
        const row = rows.find((r) => r.id === userId);
        const original = row?.originalGroupId ?? "";
        recomputeUserContribution(userId, original, groupId);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("userController.roles.info")}
            </Typography>

            <Paper
                variant="outlined"
                sx={{ minHeight: 240, display: "flex", alignItems: "stretch" }}
            >
                {loading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t("userController.loading.roles")}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 360, overflow: "auto", width: "100%" }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("userController.roles.headers.user")}</TableCell>
                                    <TableCell>{t("userController.roles.headers.name")}</TableCell>
                                    <TableCell>{t("userController.roles.headers.role")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("userController.table.noUsers")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((u) => {
                                        const value = u.selectedGroupId;
                                        return (
                                            <TableRow key={u.id} hover>
                                                <TableCell sx={{ fontFamily: "monospace" }}>
                                                    {u.mail}
                                                </TableCell>
                                                <TableCell>{u.name || "-"}</TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 1,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <FormControl fullWidth>
                                                            <InputLabel id={`role-${u.id}`}>
                                                                {t(
                                                                    "userController.roles.roleLabel"
                                                                )}
                                                            </InputLabel>
                                                            <Select
                                                                labelId={`role-${u.id}`}
                                                                label={t(
                                                                    "userController.roles.roleLabel"
                                                                )}
                                                                value={value}
                                                                displayEmpty
                                                                renderValue={(selected) => {
                                                                    const s = selected as string;
                                                                    return (
                                                                        roles.find(
                                                                            (r) =>
                                                                                r.principalId === s
                                                                        )?.principalDisplayName ?? s
                                                                    );
                                                                }}
                                                                onChange={(e) =>
                                                                    updateRowRole(
                                                                        u.id,
                                                                        e.target.value as string
                                                                    )
                                                                }
                                                            >
                                                                <MenuItem
                                                                    value=""
                                                                    sx={{ display: "none" }}
                                                                />
                                                                {roles.map((role) => (
                                                                    <MenuItem
                                                                        key={role.id}
                                                                        value={role.principalId}
                                                                    >
                                                                        {role.principalDisplayName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>

                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => updateRowRole(u.id, "")}
                                                            disabled={!u.selectedGroupId}
                                                            aria-label={t(
                                                                "userController.roles.removeRole"
                                                            )}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
